"use client";
import { TextInput } from "../inputs/text-input";
import { Formik, Form, useFormikContext } from "formik";
import { useRouter, useSearchParams } from "next/navigation";
import { Button } from "../ui/button";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { CheckboxInput } from "../inputs/checkbox";
import Link from "next/link";
import { Loader2 } from "lucide-react";
import { PasswordInput } from "../inputs/password";
import { useEffect, useState } from "react";
import { signupFormValidationSchema, SignupResponseSchema } from "./schemas/signup-schemas";
import { trackMetaEventById } from "@/eventHelpers/trackMetaEventById";
import { z } from "zod";
import { toast } from "sonner";
import { InferType } from "yup";
import { v4 as uuid } from "uuid";
import WhatsappLinkComp from "./whatsapp-link-comp";
import AuthProcessHeader from "./auth-process-header";
import { getCurrentOrStoredUtmParams, getChannelType } from "@/lib/analytics/utmParams";
import { isMixpanelProduction, logMixpanelDevEvent } from "@/lib/mixpanel/devLogger";
import mixpanel from "@/lib/mixpanel/mixpanel.client";
import { AsyncValidatedField } from "./validated-field";
import { GoogleSignupButton } from "./google-signup";
import { FEATURES } from "@/lib/config/features";

type SignupFields = InferType<typeof signupFormValidationSchema>;

const PENDING_SIGNUP_PROPERTIES_KEY = "pending_signup_mixpanel_properties";
const PENDING_ANONYMOUS_ID_KEY = "pending_mixpanel_anonymous_id";

/**
 * Syncs autofilled values once the browser has applied autofill, then lets Formik validate them normally.
 */
function SignupAutofillSync() {
  const { values, setValues, validateForm } = useFormikContext<SignupFields>();

  useEffect(() => {
    const fieldNames: Array<keyof SignupFields> = [
      "firstName",
      "lastName",
      "email",
      "username",
      "password",
      "referralCode",
    ];

    const syncAutofilledValues = () => {
      const form = document.querySelector<HTMLFormElement>("form[data-signup-form]");
      if (!form) return;

      let changed = false;
      const nextValues = { ...values } as Record<string, unknown>;
      for (const fieldName of fieldNames) {
        const input = form.elements.namedItem(fieldName) as HTMLInputElement | null;
        const domValue = input?.value ?? "";
        if (domValue && !values[fieldName]) {
          nextValues[fieldName] = domValue;
          changed = true;
        }
      }

      if (changed) {
        setValues(nextValues as SignupFields, false);
        void validateForm(nextValues as SignupFields);
      }
    };

    // Autofill is applied after initial paint in several mobile browsers.
    const timers = [0, 250, 750].map((delay) =>
      window.setTimeout(syncAutofilledValues, delay),
    );
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [setValues, validateForm, values]);

  return null;
}

export function SignupForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [isNavigating, setIsNavigating] = useState(false);
  const referralFromUrl = searchParams.get("ref") || "";
  const termsLink = process.env.NEXT_PUBLIC_TERMS_OF_SERVICE_URL;
  const privacyLink = process.env.NEXT_PUBLIC_PRIVACY_POLICY_URL;

  const handleSignup = async (values: SignupFields) => {
    const normalizedEmail = values.email.trim().toLowerCase();
    const utmParams = getCurrentOrStoredUtmParams(searchParams);
    const channelType = getChannelType(utmParams.utm_medium, utmParams);

    const payload = {
      ...values,
      email: normalizedEmail,
    };

    const toastId = toast.loading("Creating account...", {
      position: "top-center",
      classNames: {
        title: "font-outfit font-medium relative bottom-[0.6]",
        content: "flex items-center",
      },
    });

    const metaEventId = uuid();

    try {
      const response = await fetch("/api/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...payload,
          metaEventId, // add meta event id for tracking
          ...utmParams,
        }),
      });

      const data = SignupResponseSchema.parse(await response.json());

      if (data.session_token) {
        sessionStorage.setItem("pending_session_token", data.session_token)
      }
      if (data.verification_flow_id) {
        sessionStorage.setItem("pending_verification_flow_id", data.verification_flow_id)
      }

      sessionStorage.setItem("pending_credentials", JSON.stringify({
        email: normalizedEmail,
        password: values.password
      }))
      
      if (!response.ok) {
        toast.error(data.message || "Something went wrong", { id: toastId });
        return;
      }

      if (!isMixpanelProduction && data.mixpanelPayload) {
        logMixpanelDevEvent("client", "SignupStarted", data.mixpanelPayload);
      }

      if (data.isTrackedInMeta) {
        trackMetaEventById(metaEventId, "Lead");
      }

      sessionStorage.setItem(
        PENDING_SIGNUP_PROPERTIES_KEY,
        JSON.stringify({
          email: normalizedEmail,
          first_name: values.firstName,
          last_name: values.lastName,
          username: values.username,
          referral_code: values.referralCode,
          channel_type: channelType,
          utm_source: utmParams.utm_source || null,
          utm_medium: utmParams.utm_medium || null,
          utm_campaign: utmParams.utm_campaign || null,
          utm_content: utmParams.utm_content || null,
          utm_term: utmParams.utm_term || null,
          gclid: utmParams.gclid || null,
          fbclid: utmParams.fbclid || null,
          ttclid: utmParams.ttclid || null,
        }),
      );

      // Capture Mixpanel's anonymous distinct_id so we can alias it to the email on first login.
      // This connects all pre-signup pageviews and events to the same user profile.
      try {
        const anonymousId = mixpanel.get_distinct_id?.();
        if (anonymousId) {
          sessionStorage.setItem(PENDING_ANONYMOUS_ID_KEY, anonymousId);
        }
      } catch {
        // Mixpanel may not be initialized yet — safe to skip.
      }

      toast.success(data.message, { id: toastId });
      setIsNavigating(true);
      router.push(`/verify-email?email=${normalizedEmail}&type=verification`)
    } catch (e) {
      console.error(e);
      setIsNavigating(false);
      if (e instanceof z.ZodError) {
        toast.error("Validation error occurred", { id: toastId });
      } else {
        toast.error("Server error occurred", { id: toastId });
      }
    }
  };

  return (
    <>
      <Formik<SignupFields>
        validationSchema={signupFormValidationSchema}
        initialValues={{
          firstName: "",
          lastName: "",
          email: "",
          username: "",
          password: "",
          referralCode: referralFromUrl,
          subscribeToNewsletter: true,
        }}
        enableReinitialize={true}
        onSubmit={handleSignup}
      >
        {({ isSubmitting, isValid }) => {
          const isLoading = isSubmitting || isNavigating;

          return (
            <Form data-signup-form className="w-full flex flex-col items-center justify-center gap-6 mx-auto sm:py-8 max-w-[460px]">
              <SignupAutofillSync />
              <AuthProcessHeader
                heading="Buy from China with confidence"
                subHeading="Join 16,000+ businesses that source, pay, inspect and ship from China with Proc360."
              />
              {FEATURES.ENABLE_GOOGLE_AUTH && (
                <div className="lg:my-6 w-full flex flex-col gap-6">
                  <GoogleSignupButton />
                  <span className="text-center text-secondary-text-700">Or</span>
                </div>
              )}

              <AsyncValidatedField
                name="email"
                label="Email Address"
                placeholder="example@email.com"
                type="email"
                endpoint="/user/auth/check-email-exists"
                paramName="email"
                errorMessage="An account with this email already exists."
                isMobile={isMobile}
                normalize={(v) => v.trim().toLowerCase()}
                isValueCheckable={(v) => z.string().email().safeParse(v).success}
                autoComplete="email"
              />
              <div className="flex flex-row w-full justify-between gap-6 mb-2">
                <TextInput
                  name="firstName"
                  label="Legal First Name"
                  size={isMobile ? "sm" : "lg"}
                  placeholder="Enter first name"
                  autoComplete="given-name"
                  helperText="As it apears on your ID"
                />
                <TextInput
                  name="lastName"
                  label="Legal Surname"
                  size={isMobile ? "sm" : "lg"}
                  placeholder="Enter surname"
                  autoComplete="family-name"
                />
              </div>

              <AsyncValidatedField
                name="username"
                label="Username"
                placeholder="Enter Username"
                endpoint="/user/auth/check-username-exists"
                paramName="username"
                errorMessage="This username already exists. Please choose another one."
                isMobile={isMobile}
                isValueCheckable={(v) => v.trim().length >= 3}
                autoComplete="username"
              />

              <PasswordInput
                name="password"
                placeholder="**********"
                label="Password"
                size={isMobile ? "sm" : "lg"}
                autoComplete="new-password"
                showRequirements={true}
              />

              <TextInput
                label="Referral Code"
                name="referralCode"
                helperText="If you don‘t have a referral code, please skip this step"
                placeholder="Enter code"
                size={isMobile ? "sm" : "lg"}
                readOnly={!!referralFromUrl}
                autoComplete="off"
              />
              
              <div className="flex flex-row gap-2 w-full my-2">
                <CheckboxInput label="" name="subscribeToNewsletter">
                  <span className="text-[12px]">Send me import tips and Proc360 news</span>
                </CheckboxInput>
                
              </div>
              <Button
                type="submit"
                radius="full"
                className="w-full h-[44px] flex items-center justify-center gap-2 mt-3"
                size="xl"
                disabled={!isValid || isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Please wait...</span>
                  </>
                ) : (
                  "Create Account"
                )}
              </Button>
              
              <div className="flex flex-col items-center justify-center gap-2 text-center lg:mt-2">
                <div className="text-primary">
                  <span>Already have an account?</span>{" "}
                  <Link
                    href="/sign-in"
                    className="text-primary underline-offset-4 hover:underline"
                  >
                    Sign in
                  </Link>
                </div>
    
                <p className="text-xs text-muted-foreground">
                  By continuing, you agree to Proc360’s{" "}
                  <a
                    href={termsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline text-primary"
                  >
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a
                    href={privacyLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline text-primary"
                  >
                    Privacy Policy
                  </a>
                </p>
              </div>
            </Form>
          )
        }}
      </Formik>

      <WhatsappLinkComp />
    </>
  );
}
